import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, Filter, ChevronLeft, ChevronRight, Info, Check, X, AlertTriangle } from 'lucide-react';
import Evidence360Modal from './Evidence360Modal';
import { sw, P } from '../../lib/sitewalkApi';

const STATUS_STYLE = {
  PENDING_REVIEW: 'bg-amber-100 text-amber-700', ACCEPTED: 'bg-emerald-100 text-emerald-700',
  CORRECTED: 'bg-sky-100 text-sky-700', REJECTED: 'bg-red-100 text-red-700',
  INSUFFICIENT_EVIDENCE: 'bg-slate-200 text-slate-600', AI_UNAVAILABLE: 'bg-slate-200 text-slate-600',
};
const INTENTS = ['GENERAL_SITE_REVIEW', 'VISUAL_CHANGE', 'PROGRESS_EVIDENCE', 'QUALITY_REVIEW', 'SAFETY_REVIEW', 'HOUSEKEEPING', 'ACCESS_OBSTRUCTION', 'FINISHING_WORKMANSHIP'];
const STATUSES = ['PENDING_REVIEW', 'ACCEPTED', 'CORRECTED', 'REJECTED', 'INSUFFICIENT_EVIDENCE', 'AI_UNAVAILABLE'];

/** Centralized 360 AI Review Queue (Phase 7C governed). Live inference is CONFIGURATION-DEPENDENT. */
export default function AiReviewQueue({ projectId }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 20 });
  const [intent, setIntent] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await sw.aiProposals(projectId, { intent: intent || undefined, status: status || undefined, page, pageSize: 20 })); }
    catch (e) { toast.error('Failed to load queue'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, intent, status, page]);

  const decide = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${P(projectId)}/sitewalk-ai/proposals/${active.id}/review`, { action, reason });
      toast.success(`Marked ${action.replace('_', ' ').toLowerCase()}`);
      setActive(null); setReason(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
    setBusy(false);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <div data-testid="ai-review-queue">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <select value={intent} onChange={(e) => { setPage(1); setIntent(e.target.value); }} className="border rounded-lg px-2 py-1 text-xs" data-testid="ai-filter-intent">
          <option value="">All intents</option>{INTENTS.map((i) => <option key={i} value={i}>{i.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="border rounded-lg px-2 py-1 text-xs" data-testid="ai-filter-status">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <span className="text-[11px] text-slate-400 ml-auto flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Live AI inference is configuration-dependent; human review is always available.</span>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading queue…</p> : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="ai-queue-list">
            {(data.items || []).map((p) => (
              <button key={p.id} onClick={() => { setActive(p); setReason(''); }} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between" data-testid={`ai-queue-item-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700">{(p.analysisIntent || '').replace(/_/g, ' ')}</div>
                  <div className="text-[11px] text-slate-400 truncate">{(p.createdAt || '').slice(0, 10)} · walk {(p.siteWalkId || '').slice(0, 6)} · {p.inputQuality || p.aiOutcome || 'pending'}</div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${STATUS_STYLE[p.reviewStatus] || 'bg-slate-100'}`}>{(p.reviewStatus || '').replace(/_/g, ' ')}</span>
              </button>
            ))}
            {(data.items || []).length === 0 && <p className="text-sm text-slate-400 p-4 text-center">No AI proposals match these filters.</p>}
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
            <span>{data.total} proposal(s)</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 rounded border disabled:opacity-30" data-testid="ai-queue-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span>Page {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1 rounded border disabled:opacity-30" data-testid="ai-queue-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}

      {active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-6" data-testid="ai-review-panel">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><Bot className="w-5 h-5 text-violet-600" /> AI Proposal Review</h3>
              <button onClick={() => setActive(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[active.reviewStatus]}`}>{(active.reviewStatus || '').replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-500">{(active.analysisIntent || '').replace(/_/g, ' ')}</span>
              </div>
              {active.aiOutcome === 'AI_UNAVAILABLE' && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1" data-testid="ai-unavailable-note">
                  <AlertTriangle className="w-3.5 h-3.5" /> AI service not configured — this is a human-review placeholder, not live inference.
                </div>
              )}
              {(active.proposals || []).map((pr, i) => (
                <div key={i} className="text-xs bg-slate-50 rounded p-2 border border-slate-100">
                  <div className="font-medium text-slate-700">{pr.classification || pr.type || 'Observation'}</div>
                  <div className="text-slate-500">{pr.description}</div>
                </div>
              ))}
              {active.sourcePanoramaId && (
                <button onClick={() => setEvidence({ panoramaId: active.sourcePanoramaId, capturePointId: active.capturePointId })} className="text-xs px-3 py-1.5 rounded-lg border border-sky-300 text-sky-700" data-testid="ai-review-evidence">
                  Inspect 360 evidence
                </button>
              )}
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reviewer note (optional)…" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="ai-review-reason" />
              <div className="grid grid-cols-2 gap-2">
                <button disabled={busy} onClick={() => decide('ACCEPTED')} className="flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs" data-testid="ai-review-accept"><Check className="w-3.5 h-3.5" /> Accept</button>
                <button disabled={busy} onClick={() => decide('CORRECTED')} className="px-3 py-2 bg-sky-600 text-white rounded-lg text-xs" data-testid="ai-review-correct">Correct</button>
                <button disabled={busy} onClick={() => decide('REJECTED')} className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs" data-testid="ai-review-reject">Reject</button>
                <button disabled={busy} onClick={() => decide('INSUFFICIENT_EVIDENCE')} className="px-3 py-2 bg-slate-500 text-white rounded-lg text-xs" data-testid="ai-review-insufficient">Insufficient</button>
              </div>
              <p className="text-[10px] text-slate-400">The AI original is never overwritten. Confirming into an authoritative record requires the downstream create permission and is done from the Site Walk review flow.</p>
            </div>
          </div>
        </div>
      )}
      {evidence && <Evidence360Modal projectId={projectId} params={evidence} onClose={() => setEvidence(null)} />}
    </div>
  );
}
